import { detach } from '@syncfusion/ej2-base';
import { getBlockIndexById, getBlockModelById, isListTypeBlock } from '../../../common/utils/block';
import { events } from '../../../common/constant';
import { getNormalizedKey } from '../../../common/utils/common';
import * as constants from '../../../common/constant';
/**
 * `BlockActionMenuModule` is used to handle the block action menu in the BlockEditor.
 *
 * @hidden
 */
export class BlockActionMenuModule {
    constructor(manager) {
        this.isPopupOpened = false;
        this.shortcutMap = new Map();
        this.parent = manager;
        this.addEventListeners();
    }
    addEventListeners() {
        this.parent.observer.on(events.keydown, this.onKeyDown, this);
        this.parent.observer.on('actionMenuCreated', this.handleMenuCreated, this);
        this.parent.observer.on('popupWidthChanged', this.handlePopupWidthChanges, this);
        this.parent.observer.on('popupHeightChanged', this.handlePopupHeightChanges, this);
        this.parent.observer.on('blockActionsMenuSelect', this.handleBlockActionMenuSelect, this);
        this.parent.observer.on(events.destroy, this.destroy, this);
    }
    removeEventListeners() {
        this.parent.observer.off(events.keydown, this.onKeyDown);
        this.parent.observer.off('actionMenuCreated', this.handleMenuCreated);
        this.parent.observer.off('popupWidthChanged', this.handlePopupWidthChanges);
        this.parent.observer.off('popupHeightChanged', this.handlePopupHeightChanges);
        this.parent.observer.off('blockActionsMenuSelect', this.handleBlockActionMenuSelect);
        this.parent.observer.off(events.destroy, this.destroy);
    }
    handleMenuCreated() {
        this.menuWrapperElement = this.parent.rootEditorElement.querySelector(`.${constants.BLOCKACTION_MENUBAR_CLS}`);
        this.init();
        this.buildShortcutMap();
    }
    init() {
        const popupElement = document.querySelector(`#${this.parent.rootEditorElement.id + constants.BLOCKACTION_POPUP_ID}`);
        const args = {
            relateTo: this.parent.rootEditorElement,
            element: popupElement,
            content: this.menuWrapperElement,
            width: this.parent.blockActionMenuSettings.popupWidth,
            height: this.parent.blockActionMenuSettings.popupHeight
        };
        this.popupObj = this.parent.popupRenderer.renderPopup(args);
    }
    buildShortcutMap() {
        this.shortcutMap.clear();
        this.parent.blockActionMenuSettings.items.forEach((item) => {
            this.shortcutMap.set(item.shortcut.toLowerCase(), item);
        });
    }
    onKeyDown(e) {
        const normalizedKey = getNormalizedKey(e);
        const isTable = this.parent.currentFocusedBlock &&
            this.parent.currentFocusedBlock.closest(`.${constants.TABLE_BLOCK_CLS}`);
        if (!normalizedKey || isTable) {
            return;
        }
        const actionItem = this.shortcutMap.get(normalizedKey);
        if (actionItem) {
            e.preventDefault();
            this.handleBlockActions(actionItem, this.parent.currentHoveredBlock, e);
        }
    }
    /**
     * Toggles the block action popup based on the provided flag.
     *
     * @param {boolean} shouldHide - Flag indicating whether to hide or show the popup.
     * @param {Event} e - Optional event object.
     * @returns {void}
     * @hidden
     */
    toggleBlockActionPopup(shouldHide, e) {
        setTimeout(() => {
            if (this.parent && this.parent.inlineToolbarModule) {
                this.parent.inlineToolbarModule.hideInlineToolbar();
            }
        }, 10);
        if (this.popupObj) {
            if (shouldHide) {
                const closeEventArgs = {
                    event: e,
                    items: this.parent.blockActionMenuSettings.items,
                    cancel: false,
                    callback: (args) => {
                        if (args.cancel) {
                            return;
                        }
                        this.parent.selectionOverlay.clearSelectionOverlay();
                        this.popupObj.hide();
                        this.isPopupOpened = false;
                    }
                };
                this.parent.observer.notify('blockActionsMenuClose', closeEventArgs);
            }
            else {
                const openEventArgs = {
                    event: e,
                    items: this.parent.blockActionMenuSettings.items,
                    cancel: false,
                    callback: (args) => {
                        if (args.cancel) {
                            return;
                        }
                        this.toggleDisabledItems(this.parent.currentHoveredBlock);
                        this.popupObj.show();
                        this.isPopupOpened = true;
                        setTimeout(() => {
                            const items = this.popupObj.element.querySelectorAll('.e-menu-item');
                            if (items.length > 0) {
                                items[0].focus();
                                items[0].classList.add('e-focused');
                            }
                        });
                    }
                };
                this.parent.observer.notify('blockActionsMenuOpen', openEventArgs);
            }
        }
    }
    getParentBlock(parentId) {
        return getBlockModelById(parentId, this.parent.getEditorBlocks());
    }
    isFirstChildBlock(block, parentBlock) {
        const children = parentBlock.properties.children;
        return (children.length > 0 && children[0].id === block.id);
    }
    isLastChildBlock(block, parentBlock) {
        const children = parentBlock.properties.children;
        return (children.length > 0 && children[children.length - 1].id === block.id);
    }
    toggleMenuItemClass(itemId, disable) {
        const listElement = this.popupObj.element.querySelector(`#${itemId}`);
        if (listElement) {
            listElement.classList.toggle(constants.DISABLED_CLS, disable);
        }
    }
    getBlockPositionInfo(blockElement) {
        const allBlocks = this.parent.getEditorBlocks();
        const currentBlock = getBlockModelById(blockElement.id, allBlocks);
        const currentBlockParent = this.getParentBlock(currentBlock.parentId);
        const currentBlockIndex = getBlockIndexById(blockElement.id, allBlocks);
        const isFirstBlock = currentBlockIndex === 0;
        const isLastBlock = currentBlockIndex === (currentBlockParent
            ? currentBlockParent.properties.children.length - 1
            : allBlocks.length - 1);
        const hasOnlyOneBlock = allBlocks.length === 1;
        return { currentBlock, currentBlockParent, isFirstBlock, isLastBlock, hasOnlyOneBlock };
    }
    toggleDisabledItems(blockElement) {
        if (!blockElement) {
            return;
        }
        const selectedBlocks = this.parent.editorMethods.getSelectedBlocks();
        if (selectedBlocks && selectedBlocks.length > 1) {
            for (const item of this.parent.blockActionMenuSettings.items) {
                this.toggleMenuItemClass(item.id, true);
            }
            return;
        }
        const { currentBlock, currentBlockParent, isFirstBlock, isLastBlock, hasOnlyOneBlock } = this.getBlockPositionInfo(blockElement);
        for (const item of this.parent.blockActionMenuSettings.items) {
            let disable = item.disabled;
            switch (item.id) {
                case 'moveup':
                    disable = hasOnlyOneBlock || isFirstBlock;
                    if (currentBlockParent && this.isFirstChildBlock(currentBlock, currentBlockParent)) {
                        disable = true;
                    }
                    break;
                case 'movedown':
                    disable = hasOnlyOneBlock || isLastBlock;
                    if (currentBlockParent && this.isLastChildBlock(currentBlock, currentBlockParent)) {
                        disable = true;
                    }
                    break;
            }
            this.toggleMenuItemClass(item.id, disable);
        }
    }
    handleBlockActionMenuSelect(args) {
        this.handleBlockActions(args.item, this.parent.currentHoveredBlock, args.event);
    }
    handlePopupWidthChanges(data) {
        this.popupObj.width = this.parent.blockActionMenuSettings.popupWidth = data.value.toString();
    }
    handlePopupHeightChanges(data) {
        this.popupObj.height = this.parent.blockActionMenuSettings.popupWidth = data.value.toString();
    }
    handleBlockActions(item, blockElement, e) {
        const selectedItem = item.label.replace(' ', '').toLowerCase();
        let toBlockElement;
        let toBlockModel;
        switch (selectedItem) {
            case 'duplicate':
                this.parent.execCommand({ command: 'DuplicateBlock', state: {
                        blockElement, direction: 'below'
                    } });
                break;
            case 'delete': {
                const adjacentBlock = (blockElement.nextElementSibling || blockElement.previousElementSibling);
                if (adjacentBlock) {
                    this.parent.setFocusAndUIForNewBlock(adjacentBlock);
                }
                this.parent.execCommand({ command: 'DeleteBlock', state: { blockElement } });
                break;
            }
            case 'moveup':
            case 'movedown': {
                this.toggleDisabledItems(blockElement);
                if (!blockElement || this.isItemDisabled(item.id)) {
                    return;
                }
                toBlockElement = (selectedItem === 'moveup' ? blockElement.previousElementSibling : blockElement.nextElementSibling);
                if (toBlockElement) {
                    toBlockModel = getBlockModelById(toBlockElement.id, this.parent.getEditorBlocks());
                    this.parent.execCommand({ command: 'MoveBlock', state: {
                            fromBlockIds: [blockElement.id],
                            toBlockId: toBlockElement.id
                        } });
                }
                break;
            }
        }
        const currentBlockModel = getBlockModelById(blockElement.id, this.parent.getEditorBlocks());
        if ((currentBlockModel && isListTypeBlock(currentBlockModel.blockType)) ||
            (toBlockModel && isListTypeBlock(toBlockModel.blockType))) {
            this.parent.listPlugin.recalculateMarkersForListItems();
        }
        this.toggleBlockActionPopup(true, e);
    }
    isItemDisabled(itemId) {
        const listElement = this.popupObj.element.querySelector('#' + itemId);
        return listElement && listElement.classList.contains(constants.DISABLED_CLS);
    }
    /**
     * Checks whether the block action popup is opened or not.
     *
     * @returns {boolean} - Returns true if the block action popup is opened, otherwise false.
     * @hidden
     */
    isPopupOpen() {
        return this.isPopupOpened;
    }
    destroy() {
        this.removeEventListeners();
        if (this.popupObj) {
            this.popupObj.destroy();
            detach(this.popupObj.element);
            this.popupObj = null;
        }
        this.shortcutMap = null;
    }
}
